import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../utils/useAuth";

export default function ManagerBilling() {
  const { user, profile } = useAuth();
  const [games, setGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [showInvoicePopup, setShowInvoicePopup] = useState(false);
  const [travelFees, setTravelFees] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    companyName: "RefCall",
    logoUrl: "",
    address: ""
  });
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [invoicesExpanded, setInvoicesExpanded] = useState(true);
  const [showSchoolDetailsPopup, setShowSchoolDetailsPopup] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [billingSchool, setBillingSchool] = useState("");
  const [allTeams, setAllTeams] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Load completed games with billing
  useEffect(() => {
    const q = query(
      collection(db, "games"),
      where("status", "==", "completed")
    );

    const unsub = onSnapshot(q, (snap) => {
      const loadedGames = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log("Completed games loaded:", loadedGames.length);
      console.log("Games data:", loadedGames);
      setGames(loadedGames);
    });

    return unsub;
  }, []);

  // Load all teams
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "teams"), (snap) => {
      const teams = snap.docs.map((d) => d.data().name).sort();
      setAllTeams(teams);
    });
    return unsub;
  }, []);

  // Load invoices
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "invoices"), (snap) => {
      setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, []);

  // Load company settings
  useEffect(() => {
    async function loadSettings() {
      if (!user) return;
      try {
        const settingsDoc = await getDoc(doc(db, "companySettings", user.uid));
        if (settingsDoc.exists()) {
          setCompanySettings(settingsDoc.data());
        }
      } catch (err) {
        console.error("Error loading settings:", err);
      }
    }
    loadSettings();
  }, [user]);

  // Calculate totals for each game
  function calculateGameTotal(game) {
    let total = 0;
    if (game.billing_referee) total += parseInt(game.billing_referee);
    if (game.billing_assistant1) total += parseInt(game.billing_assistant1);
    if (game.billing_assistant2) total += parseInt(game.billing_assistant2);
    if (game.billing_assistant4) total += parseInt(game.billing_assistant4);
    if (game.billing_assistant5) total += parseInt(game.billing_assistant5);
    if (game.billing_timekeeper) total += parseInt(game.billing_timekeeper);
    if (game.billing_playermovement) total += parseInt(game.billing_playermovement);
    if (game.billing_statistician) total += parseInt(game.billing_statistician);
    if (game.billing_technicalzone) total += parseInt(game.billing_technicalzone);
    if (game.billing_pointoforigin1) total += parseInt(game.billing_pointoforigin1);
    if (game.billing_pointoforigin2) total += parseInt(game.billing_pointoforigin2);
    return total;
  }

  // Calculate overall totals
  const allGamesWithBilling = games.filter(g => calculateGameTotal(g) > 0);
  const totalBilled = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const settledInvoices = invoices.filter(inv => inv.status === 'settled');
  const totalSettled = settledInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  
  // Calculate billing report per school
  const billingReport = {};
  
  invoices.forEach(invoice => {
    // Use billingSchool from invoice (new invoices) or fall back to teamB (old invoices)
    const school = invoice.billingSchool || invoice.games?.[0]?.teamB || "Unknown School";
    
    if (!billingReport[school]) {
      billingReport[school] = {
        invoiceIds: new Set(),
        totalDue: 0,
        totalPaid: 0
      };
    }
    
    // Only count each invoice once per school
    if (!billingReport[school].invoiceIds.has(invoice.id)) {
      billingReport[school].invoiceIds.add(invoice.id);
      billingReport[school].totalDue += invoice.total;
      
      // If invoice is settled, use total as paid amount (in case amountPaid is incorrect)
      // Otherwise use amountPaid
      const paidAmount = invoice.status === 'settled' 
        ? invoice.total 
        : (invoice.amountPaid || 0);
      
      billingReport[school].totalPaid += paidAmount;
    }
  });

  const reportData = Object.entries(billingReport).map(([school, data]) => ({
    school,
    totalInvoices: data.invoiceIds.size,
    totalDue: data.totalDue,
    totalPaid: data.totalPaid,
    totalOutstanding: data.totalDue - data.totalPaid
  })).sort((a, b) => a.school.localeCompare(b.school));
  
  // Filter out games that are already invoiced
  const uninvoicedGames = games.filter(g => !g.invoiceId && calculateGameTotal(g) > 0);
  
  // Apply school filter
  const gamesWithBilling = schoolFilter === "all" 
    ? uninvoicedGames 
    : uninvoicedGames.filter(g => g.coachName === schoolFilter);
  
  // Get unique schools/coaches
  const uniqueSchools = [...new Set(games.map(g => g.coachName))].sort();

  // Open school details popup
  function openSchoolDetails(schoolName) {
    setSelectedSchool(schoolName);
    setShowSchoolDetailsPopup(true);
  }

  // Get invoices for selected school
  const schoolInvoices = selectedSchool
    ? invoices.filter(inv => 
        inv.games.some(game => game.teamB === selectedSchool)
      )
    : [];

  // Toggle game selection
  function toggleGameSelection(gameId) {
    setSelectedGames(prev => 
      prev.includes(gameId) 
        ? prev.filter(id => id !== gameId)
        : [...prev, gameId]
    );
  }

  // Select/deselect all
  function toggleSelectAll() {
    if (selectedGames.length === gamesWithBilling.length) {
      setSelectedGames([]);
    } else {
      setSelectedGames(gamesWithBilling.map(g => g.id));
    }
  }

  // Calculate selected games total
  const selectedGamesData = games.filter(g => selectedGames.includes(g.id));
  const selectedTotal = selectedGamesData.reduce((sum, g) => sum + calculateGameTotal(g), 0);

  // Open invoice popup
  function openInvoicePopup() {
    if (selectedGames.length === 0) {
      alert("Please select at least one game to create an invoice.");
      return;
    }
    setShowInvoicePopup(true);
    setTravelFees("");
    setBillingSchool("");
  }

  // Generate and save invoice
  async function createInvoice() {
    if (!billingSchool) {
      alert("Please select a school/club to bill.");
      return;
    }

    try {
      const travelAmount = travelFees ? parseInt(travelFees) : 0;
      const invoiceTotal = selectedTotal + travelAmount;

      // Create invoice document
      const invoiceData = {
        billingSchool: billingSchool,
        games: selectedGamesData.map(g => ({
          id: g.id,
          teamA: g.teamA,
          teamB: g.teamB,
          date: g.date,
          time: g.time,
          venue: g.venue,
          coachId: g.coachId,
          coachName: g.coachName,
          total: calculateGameTotal(g),
          breakdown: {
            referee: g.billing_referee || 0,
            assistant1: g.billing_assistant1 || 0,
            assistant2: g.billing_assistant2 || 0,
            assistant4: g.billing_assistant4 || 0,
            assistant5: g.billing_assistant5 || 0,
            timekeeper: g.billing_timekeeper || 0,
            playermovement: g.billing_playermovement || 0,
            statistician: g.billing_statistician || 0,
            technicalzone: g.billing_technicalzone || 0,
            pointoforigin1: g.billing_pointoforigin1 || 0,
            pointoforigin2: g.billing_pointoforigin2 || 0
          }
        })),
        travelFees: travelAmount,
        subtotal: selectedTotal,
        total: invoiceTotal,
        amountPaid: 0,
        createdAt: serverTimestamp(),
        status: "invoiced",
        companyName: companySettings.companyName,
        logoUrl: companySettings.logoUrl,
        address: companySettings.address
      };

      const invoiceRef = await addDoc(collection(db, "invoices"), invoiceData);

      // Mark games as invoiced
      for (const gameId of selectedGames) {
        await updateDoc(doc(db, "games", gameId), {
          invoiceId: invoiceRef.id
        });
      }

      alert(`Invoice created successfully! Total: R${invoiceTotal.toLocaleString()}`);
      
      // Reset
      setSelectedGames([]);
      setShowInvoicePopup(false);
      setTravelFees("");
    } catch (err) {
      console.error("Error creating invoice:", err);
      alert("Failed to create invoice: " + err.message);
    }
  }

  // Save company settings
  async function saveCompanySettings() {
    try {
      if (!user) return;
      
      // Use setDoc with merge to ensure settings are saved properly
      await setDoc(doc(db, "companySettings", user.uid), companySettings, { merge: true });
      
      alert("Company settings saved!");
      setShowSettingsPopup(false);
    } catch (err) {
      console.error("Error saving settings:", err);
      alert("Failed to save settings: " + err.message);
    }
  }

  // Handle logo file upload - convert to base64 and store in Firestore
  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      return;
    }

    try {
      setUploadingLogo(true);
      
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Image = event.target.result;
        
        // Update company settings with base64 image
        setCompanySettings({...companySettings, logoUrl: base64Image});
        
        setUploadingLogo(false);
        alert('Logo uploaded successfully!');
      };
      
      reader.onerror = () => {
        console.error('Error reading file');
        alert('Failed to read image file');
        setUploadingLogo(false);
      };
      
      reader.readAsDataURL(file);
      
    } catch (err) {
      console.error('Error uploading logo:', err);
      alert('Failed to upload logo: ' + err.message);
      setUploadingLogo(false);
    }
  }

  // Open payment popup
  function openPaymentPopup(invoice) {
    setSelectedInvoice(invoice);
    const outstanding = invoice.total - (invoice.amountPaid || 0);
    setPaymentAmount(outstanding.toString());
    setShowPaymentPopup(true);
  }

  // Mark invoice as settled
  async function markAsSettled() {
    try {
      const payment = parseInt(paymentAmount);
      
      if (!payment || payment <= 0) {
        alert("Please enter a valid payment amount.");
        return;
      }

      const currentPaid = selectedInvoice.amountPaid || 0;
      const newAmountPaid = currentPaid + payment;
      const outstanding = selectedInvoice.total - newAmountPaid;

      if (outstanding < 0) {
        alert("Payment amount exceeds outstanding balance.");
        return;
      }

      const updateData = {
        amountPaid: newAmountPaid
      };

      // Only mark as settled if fully paid
      if (outstanding === 0) {
        updateData.status = "settled";
        updateData.settledAt = serverTimestamp();
      }

      await updateDoc(doc(db, "invoices", selectedInvoice.id), updateData);

      if (outstanding === 0) {
        alert("Invoice fully paid and settled!");
      } else {
        alert(`Payment recorded! Outstanding balance: R${outstanding.toLocaleString()}`);
      }
      
      // Reset
      setShowPaymentPopup(false);
      setSelectedInvoice(null);
      setPaymentAmount("");
    } catch (err) {
      console.error("Error processing payment:", err);
      alert("Failed to process payment: " + err.message);
    }
  }

  // Helper function to convert image URL to base64
  async function getBase64Image(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  // Generate PDF invoice
  async function downloadInvoicePDF(invoice) {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      let startY = 20;
      
      // Logo (centered) - Convert to base64 to avoid CORS issues
      if (invoice.logoUrl || companySettings.logoUrl) {
        try {
          const logoUrl = invoice.logoUrl || companySettings.logoUrl;
          const base64Logo = await getBase64Image(logoUrl);
          pdf.addImage(base64Logo, 'PNG', 75, 10, 60, 30); // Centered logo
          startY = 45;
        } catch (err) {
          console.error("Error adding logo:", err);
          startY = 20;
        }
      }
      
      // Company Address
      if (invoice.address || companySettings.address) {
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'normal');
        pdf.text(invoice.address || companySettings.address, 105, startY, { align: 'center' });
        startY += 7;
      }
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.text('INVOICE', 105, startY + 10, { align: 'center' });
      
      // Invoice details
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Invoice #: ${invoice.id.substring(0, 8).toUpperCase()}`, 20, startY + 25);
      pdf.text(`Date: ${invoice.createdAt?.toDate().toLocaleDateString()}`, 20, startY + 32);
      
      // Bill To
      if (invoice.billingSchool) {
        pdf.setFont(undefined, 'bold');
        pdf.text(`Bill To: ${invoice.billingSchool}`, 20, startY + 39);
        pdf.setFont(undefined, 'normal');
      }
      
      pdf.text(`Status: ${invoice.status.toUpperCase()}`, 20, startY + (invoice.billingSchool ? 46 : 39));
      
      if (invoice.status === 'settled' && invoice.settledAt) {
        pdf.text(`Paid: ${invoice.settledAt?.toDate().toLocaleDateString()}`, 20, startY + (invoice.billingSchool ? 53 : 46));
      }
      
      // Games table
      const gameRows = (invoice.games || []).map((game, index) => [
        index + 1,
        `${game.teamA} vs ${game.teamB}`,
        game.date,
        game.venue,
        `R${game.total}`
      ]);
    
    autoTable(pdf, {
      startY: startY + (invoice.billingSchool ? 67 : 60),
      head: [['#', 'Game', 'Date', 'Venue', 'Amount']],
      body: gameRows,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 9 }
    });
    
    // Summary
    const finalY = pdf.lastAutoTable.finalY + 10;
    pdf.setFontSize(10);
    
    pdf.text('Subtotal:', 130, finalY);
    pdf.text(`R${invoice.subtotal.toLocaleString()}`, 170, finalY, { align: 'right' });
    
    pdf.text('Travel Fees:', 130, finalY + 7);
    pdf.text(`R${invoice.travelFees.toLocaleString()}`, 170, finalY + 7, { align: 'right' });
    
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(12);
    pdf.text('TOTAL:', 130, finalY + 17);
    pdf.text(`R${invoice.total.toLocaleString()}`, 170, finalY + 17, { align: 'right' });
    
    // Footer
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'italic');
    pdf.text('Thank you for your business!', 105, 280, { align: 'center' });
    
    // Generated by footer
    pdf.setFontSize(9);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(100, 100, 100);
    const generatedBy = `Generated by: ${profile.fullName} with RefCall Software`;
    pdf.text(generatedBy, 105, 287, { align: 'center' });
    
    // Save
    pdf.save(`Invoice-${invoice.id.substring(0, 8).toUpperCase()}.pdf`);
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Failed to generate PDF: " + err.message);
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Billing Overview</h2>
        <button
          onClick={() => setShowSettingsPopup(true)}
          className="px-4 py-2 bg-gray-700 text-white rounded font-semibold hover:bg-gray-800"
        >
          ⚙️ Invoice Settings
        </button>
      </div>

      {/* INVOICES SECTION */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <div 
          className="flex justify-between items-center mb-4 border-b pb-2 cursor-pointer"
          onClick={() => setInvoicesExpanded(!invoicesExpanded)}
        >
          <h3 className="text-xl font-bold">Invoices</h3>
          <button className="text-2xl font-bold text-gray-600 hover:text-gray-800">
            {invoicesExpanded ? "−" : "+"}
          </button>
        </div>
        
        {invoicesExpanded && (
          <>
            {invoices.length === 0 ? (
              <p className="text-gray-500">No invoices created yet.</p>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice) => (
              <div 
                key={invoice.id} 
                className={`border rounded p-4 ${
                  invoice.status === "settled" 
                    ? "bg-green-50 border-green-500" 
                    : "bg-yellow-50 border-yellow-500"
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-semibold">
                      Invoice #{invoice.id.substring(0, 8).toUpperCase()}
                    </p>
                    <p className="text-xs text-gray-600">
                      {invoice.createdAt?.toDate().toLocaleDateString()} - {invoice.games.length} game(s)
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-green-700">R{invoice.total.toLocaleString()}</p>
                    <span className={`text-xs px-2 py-1 rounded font-semibold ${
                      invoice.status === "settled"
                        ? "bg-green-600 text-white"
                        : "bg-yellow-600 text-white"
                    }`}>
                      {invoice.status.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="ml-2 font-semibold">R{invoice.subtotal.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Travel Fees:</span>
                    <span className="ml-2 font-semibold">R{invoice.travelFees.toLocaleString()}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => downloadInvoicePDF(invoice)}
                    className="flex-1 bg-gray-700 text-white py-2 px-4 rounded font-semibold hover:bg-gray-800 flex items-center justify-center gap-2"
                  >
                    <span>📄</span> Download PDF
                  </button>

                  {invoice.status === "invoiced" && (
                    <button
                      onClick={() => openPaymentPopup(invoice)}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded font-semibold hover:bg-blue-700"
                    >
                      Payment Received
                    </button>
                  )}
                </div>

                {/* Payment Status */}
                <div className="mt-2 text-sm">
                  {invoice.amountPaid > 0 && (
                    <div className="font-semibold text-blue-700">
                      Paid: R{invoice.amountPaid.toLocaleString()} / R{invoice.total.toLocaleString()}
                    </div>
                  )}
                  {invoice.amountPaid > 0 && invoice.status !== "settled" && (
                    <div className="font-semibold text-orange-600">
                      Outstanding: R{(invoice.total - invoice.amountPaid).toLocaleString()}
                    </div>
                  )}
                  {invoice.status === "settled" && (
                    <div className="text-green-700 font-semibold">
                      ✓ Fully Paid on {invoice.settledAt?.toDate().toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </div>

      {/* BILLING STATS */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
        <div className="bg-blue-50 p-6 rounded shadow">
          <p className="text-sm text-gray-600 mb-2">Total Completed Games</p>
          <p className="text-3xl font-bold text-blue-700">{games.length}</p>
        </div>
        <div className="bg-green-50 p-6 rounded shadow">
          <p className="text-sm text-gray-600 mb-2">Total Invoices Created</p>
          <p className="text-3xl font-bold text-green-700">{invoices.length}</p>
        </div>
        <div className="bg-purple-50 p-6 rounded shadow">
          <p className="text-sm text-gray-600 mb-2">Total Amount Invoiced</p>
          <p className="text-3xl font-bold text-purple-700">R{totalBilled.toLocaleString()}</p>
        </div>
        <div className="bg-emerald-50 p-6 rounded shadow">
          <p className="text-sm text-gray-600 mb-2">Settled Invoices</p>
          <p className="text-3xl font-bold text-emerald-700">{settledInvoices.length}</p>
          <p className="text-sm text-emerald-600 font-semibold mt-1">R{totalSettled.toLocaleString()}</p>
        </div>
      </div>

      {/* BILLING REPORT PER SCHOOL */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h3 className="text-xl font-bold mb-4 border-b pb-2">Billing Report by School</h3>
        
        {reportData.length === 0 ? (
          <p className="text-gray-500">No billing data available yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-3 font-semibold">School</th>
                  <th className="text-center p-3 font-semibold">Total Invoices</th>
                  <th className="text-right p-3 font-semibold">Total Due</th>
                  <th className="text-right p-3 font-semibold">Total Paid</th>
                  <th className="text-right p-3 font-semibold">Outstanding</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reportData.map((row) => (
                  <tr key={row.school} className="hover:bg-gray-50">
                    <td className="p-3 font-semibold">
                      <button
                        onClick={() => openSchoolDetails(row.school)}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                      >
                        {row.school}
                      </button>
                    </td>
                    <td className="p-3 text-center">{row.totalInvoices}</td>
                    <td className="p-3 text-right font-semibold text-blue-700">
                      R{row.totalDue.toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-semibold text-green-700">
                      R{row.totalPaid.toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-semibold text-orange-700">
                      R{row.totalOutstanding.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr className="font-bold">
                  <td className="p-3">TOTALS</td>
                  <td className="p-3 text-center">{invoices.length}</td>
                  <td className="p-3 text-right text-blue-700">
                    R{reportData.reduce((sum, row) => sum + row.totalDue, 0).toFixed(2)}
                  </td>
                  <td className="p-3 text-right text-green-700">
                    R{reportData.reduce((sum, row) => sum + row.totalPaid, 0).toFixed(2)}
                  </td>
                  <td className="p-3 text-right text-orange-700">
                    R{reportData.reduce((sum, row) => sum + row.totalOutstanding, 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* BILLED GAMES LIST */}
      <div className="bg-white p-6 rounded shadow">
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h3 className="text-xl font-bold">Billed Games</h3>
          <div className="flex gap-3">
            {selectedGames.length > 0 && (
              <span className="text-sm text-gray-600 self-center">
                {selectedGames.length} selected (R{selectedTotal.toLocaleString()})
              </span>
            )}
            <button
              onClick={openInvoicePopup}
              disabled={selectedGames.length === 0}
              className={`px-4 py-2 rounded font-semibold ${
                selectedGames.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              Create Invoice
            </button>
          </div>
        </div>
        
        <div>
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold">Uninvoiced Games</h4>
            <div className="flex gap-2 items-center">
              <select
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
                className="px-3 py-1 border rounded text-sm"
              >
                <option value="all">All Schools/Clubs</option>
                {uniqueSchools.map(school => (
                  <option key={school} value={school}>{school}</option>
                ))}
              </select>
              <button
                onClick={toggleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedGames.length === gamesWithBilling.length && gamesWithBilling.length > 0 ? "Deselect All" : "Select All"}
              </button>
            </div>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {gamesWithBilling.length === 0 ? (
              <p className="text-gray-500 text-sm">No games with billing set yet.</p>
            ) : (
              gamesWithBilling.map((g) => (
                <div 
                  key={g.id} 
                  className={`border rounded p-3 transition-colors ${
                    selectedGames.includes(g.id) 
                      ? "bg-green-50 border-green-500" 
                      : "bg-gray-50 border-gray-300"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* CHECKBOX */}
                    <input
                      type="checkbox"
                      checked={selectedGames.includes(g.id)}
                      onChange={() => toggleGameSelection(g.id)}
                      className="mt-1 w-5 h-5 cursor-pointer"
                    />
                    
                    {/* GAME CONTENT */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-sm">{g.teamA} vs {g.teamB}</p>
                          <p className="text-xs text-gray-600">{g.date} @ {g.time}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-700">R{calculateGameTotal(g)}</p>
                        </div>
                      </div>
                  
                  {/* Breakdown */}
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {g.billing_referee && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">Referee:</span>
                        <span className="font-semibold">R{g.billing_referee}</span>
                      </div>
                    )}
                    {g.billing_assistant1 && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">AR1:</span>
                        <span className="font-semibold">R{g.billing_assistant1}</span>
                      </div>
                    )}
                    {g.billing_assistant2 && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">AR2:</span>
                        <span className="font-semibold">R{g.billing_assistant2}</span>
                      </div>
                    )}
                    {g.billing_assistant4 && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">A4:</span>
                        <span className="font-semibold">R{g.billing_assistant4}</span>
                      </div>
                    )}
                    {g.billing_assistant5 && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">A5:</span>
                        <span className="font-semibold">R{g.billing_assistant5}</span>
                      </div>
                    )}
                    {g.billing_timekeeper && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">TK:</span>
                        <span className="font-semibold">R{g.billing_timekeeper}</span>
                      </div>
                    )}
                    {g.billing_playermovement && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">PMR:</span>
                        <span className="font-semibold">R{g.billing_playermovement}</span>
                      </div>
                    )}
                    {g.billing_statistician && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">Stats:</span>
                        <span className="font-semibold">R{g.billing_statistician}</span>
                      </div>
                    )}
                    {g.billing_technicalzone && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">TZM:</span>
                        <span className="font-semibold">R{g.billing_technicalzone}</span>
                      </div>
                    )}
                    {g.billing_pointoforigin1 && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">PO1:</span>
                        <span className="font-semibold">R{g.billing_pointoforigin1}</span>
                      </div>
                    )}
                    {g.billing_pointoforigin2 && (
                      <div className="flex justify-between bg-white px-2 py-1 rounded">
                        <span className="text-gray-600">PO2:</span>
                        <span className="font-semibold">R{g.billing_pointoforigin2}</span>
                      </div>
                    )}
                  </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* INVOICE POPUP */}
      {showInvoicePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Create Invoice</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Selected Games: <span className="font-semibold">{selectedGames.length}</span>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Subtotal: <span className="font-semibold text-green-700">R{selectedTotal.toLocaleString()}</span>
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2 text-red-600">
                Bill To (School/Club) *
              </label>
              <select
                value={billingSchool}
                onChange={(e) => setBillingSchool(e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              >
                <option value="">-- Select School/Club --</option>
                {allTeams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                Travel Fees (Optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-600">R</span>
                <input
                  type="number"
                  value={travelFees}
                  onChange={(e) => setTravelFees(e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="mb-6 p-4 bg-green-50 rounded">
              <p className="text-lg font-bold text-green-800">
                Total: R{((selectedTotal + (travelFees ? parseInt(travelFees) : 0))).toLocaleString()}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={createInvoice}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded font-semibold hover:bg-green-700"
              >
                Save Invoice
              </button>
              <button
                onClick={() => setShowInvoicePopup(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded font-semibold hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT RECEIVED POPUP */}
      {showPaymentPopup && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Record Payment</h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600 mb-2">
                Invoice: <span className="font-semibold">#{selectedInvoice.id.substring(0, 8).toUpperCase()}</span>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Total: <span className="font-semibold">R{selectedInvoice.total.toLocaleString()}</span>
              </p>
              {selectedInvoice.amountPaid > 0 && (
                <p className="text-sm text-blue-700 mb-2">
                  Already Paid: <span className="font-semibold">R{selectedInvoice.amountPaid.toLocaleString()}</span>
                </p>
              )}
              <p className="text-lg font-bold text-orange-600">
                Outstanding: R{(selectedInvoice.total - (selectedInvoice.amountPaid || 0)).toLocaleString()}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                Payment Amount Received
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-600">R</span>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter the amount received. Invoice will auto-settle when fully paid.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={markAsSettled}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded font-semibold hover:bg-blue-700"
              >
                Record Payment
              </button>
              <button
                onClick={() => {
                  setShowPaymentPopup(false);
                  setSelectedInvoice(null);
                  setPaymentAmount("");
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded font-semibold hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SCHOOL DETAILS POPUP */}
      {showSchoolDetailsPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">Invoices for {selectedSchool}</h3>
                <button
                  onClick={() => setShowSchoolDetailsPopup(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              {schoolInvoices.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No invoices found for this school.</p>
              ) : (
                <div className="space-y-4">
                  {schoolInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className={`border rounded p-4 ${
                        invoice.status === "settled"
                          ? "bg-green-50 border-green-500"
                          : "bg-yellow-50 border-yellow-500"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-lg">
                            Invoice #{invoice.id.substring(0, 8).toUpperCase()}
                          </p>
                          <p className="text-sm text-gray-600">
                            {invoice.createdAt?.toDate().toLocaleDateString()} - {invoice.games.length} game(s)
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-xl text-green-700">R{invoice.total.toLocaleString()}</p>
                          <span
                            className={`text-xs px-2 py-1 rounded font-semibold ${
                              invoice.status === "settled"
                                ? "bg-green-600 text-white"
                                : "bg-yellow-600 text-white"
                            }`}
                          >
                            {invoice.status.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Games in this invoice */}
                      <div className="mb-3 bg-white rounded p-3">
                        <p className="font-semibold text-sm mb-2">Games:</p>
                        <div className="space-y-1">
                          {invoice.games.map((game, idx) => (
                            <div key={idx} className="text-sm flex justify-between">
                              <span>{game.date} - {game.teamA} vs {game.teamB}</span>
                              <span className="font-semibold">R{game.total}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <span className="text-gray-600">Subtotal:</span>
                          <span className="ml-2 font-semibold">R{invoice.subtotal.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Travel Fees:</span>
                          <span className="ml-2 font-semibold">R{invoice.travelFees.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Payment Status */}
                      <div className="mt-2 text-sm">
                        {invoice.amountPaid > 0 && (
                          <div className="font-semibold text-blue-700">
                            Paid: R{invoice.amountPaid.toLocaleString()} / R{invoice.total.toLocaleString()}
                          </div>
                        )}
                        {invoice.amountPaid > 0 && invoice.status !== "settled" && (
                          <div className="font-semibold text-orange-600">
                            Outstanding: R{(invoice.total - invoice.amountPaid).toLocaleString()}
                          </div>
                        )}
                        {invoice.status === "settled" && (
                          <div className="text-green-700 font-semibold">
                            ✓ Fully Paid on {invoice.settledAt?.toDate().toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => downloadInvoicePDF(invoice)}
                          className="flex-1 bg-gray-700 text-white py-2 px-4 rounded font-semibold hover:bg-gray-800 flex items-center justify-center gap-2"
                        >
                          <span>📄</span> Download PDF
                        </button>

                        {invoice.status === "invoiced" && (
                          <button
                            onClick={() => {
                              setShowSchoolDetailsPopup(false);
                              openPaymentPopup(invoice);
                            }}
                            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded font-semibold hover:bg-blue-700"
                          >
                            Payment Received
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowSchoolDetailsPopup(false)}
                className="w-full bg-gray-600 text-white py-2 px-4 rounded font-semibold hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COMPANY SETTINGS POPUP */}
      {showSettingsPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Invoice Settings</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                Company Name
              </label>
              <input
                type="text"
                value={companySettings.companyName}
                onChange={(e) => setCompanySettings({...companySettings, companyName: e.target.value})}
                placeholder="Your Company Name"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                Company Logo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload your company logo (PNG, JPG). Max size: 2MB. Recommended: 300x300px
              </p>
              {uploadingLogo && (
                <p className="text-sm text-blue-600 mt-2">Uploading...</p>
              )}
              {companySettings.logoUrl && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 mb-1">Current Logo:</p>
                  <img src={companySettings.logoUrl} alt="Logo preview" className="h-16 border rounded" />
                  <button
                    type="button"
                    onClick={() => setCompanySettings({...companySettings, logoUrl: ""})}
                    className="text-xs text-red-600 hover:text-red-700 mt-1"
                  >
                    Remove Logo
                  </button>
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                Company Address
              </label>
              <textarea
                value={companySettings.address}
                onChange={(e) => setCompanySettings({...companySettings, address: e.target.value})}
                placeholder="123 Main Street, City, Province, Postal Code"
                rows="2"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={saveCompanySettings}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded font-semibold hover:bg-blue-700"
              >
                Save Settings
              </button>
              <button
                onClick={() => setShowSettingsPopup(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded font-semibold hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
